import apiClient, { extractApiError } from './api-client'
import type { PromoQuote } from './promo-codes'

export interface BillingCheckoutRequest {
  planId: string
  provider?: string
  amountMinor?: number
  promoCode?: string
}

export interface BillingCheckoutResult {
  url: string | null
  invoice_id?: string | null
  transaction_id: number
  idempotency_key?: string | null
  amount_minor?: number
  intent_expires_at?: string | null
  status: 'ready' | 'pending' | 'completed' | 'failed' | 'refunded'
  pending?: boolean
  terminal?: boolean
  activated?: boolean
  promo?: PromoQuote | null
}

const STORAGE_PREFIX = 'mph.checkout-intent'

function storageKey(organizationId: string, request: BillingCheckoutRequest) {
  const provider = request.provider || 'monopay'
  const amount = request.amountMinor == null ? 'default' : String(request.amountMinor)
  const promo = encodeURIComponent(request.promoCode?.trim() || 'none')
  return `${STORAGE_PREFIX}.${organizationId}.${provider}.${request.planId}.${amount}.${promo}`
}

function getOrCreateIdempotencyKey(organizationId: string, request: BillingCheckoutRequest) {
  const key = storageKey(organizationId, request)
  try {
    const existing = window.sessionStorage.getItem(key)
    if (existing) return { key, idempotencyKey: existing }
    const idempotencyKey = window.crypto.randomUUID()
    window.sessionStorage.setItem(key, idempotencyKey)
    return { key, idempotencyKey }
  } catch {
    return { key, idempotencyKey: window.crypto.randomUUID() }
  }
}

function clearStoredIntent(key: string) {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Server-side active-intent uniqueness still protects a browser without sessionStorage.
  }
}

export async function initiateBillingCheckout(
  organizationId: string,
  request: BillingCheckoutRequest,
): Promise<BillingCheckoutResult> {
  const { key, idempotencyKey } = getOrCreateIdempotencyKey(organizationId, request)
  try {
    const response = await apiClient.post(
      '/api/v1/checkout/',
      {
        plan_id: request.planId,
        provider: request.provider || 'monopay',
        ...(request.amountMinor == null ? {} : { amount_minor: request.amountMinor }),
        ...(request.promoCode?.trim() ? { promo_code: request.promoCode.trim() } : {}),
      },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    const result = response.data as BillingCheckoutResult
    if (!result.pending) clearStoredIntent(key)
    return result
  } catch (error) {
    const status = extractApiError(error).status
    if (status != null && status < 500) clearStoredIntent(key)
    throw error
  }
}
